import { PublicQuoteOverview } from './PublicQuoteOverview';
import { PublicQuoteAgency } from './PublicQuoteAgency';
import { PublicQuotePdf } from './PublicQuotePdf';
import { PublicQuoteActions, quoteContactUrl } from './PublicQuoteActions';
import { useEffect, useRef, useState } from 'react';
import {
  useMediaQuery,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Paper,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type {
  PublicQuoteView,
  PublicQuoteSelection,
  PublicQuotePricing,
  PublicQuoteSnapshot,
} from '@bandido/shared/types';
import { currencyDigits } from '@bandido/shared/commercial-quotes';
import { fetchPublicQuote, evaluatePublicQuote } from './publicQuoteTransport';
import { PublicQuoteDetails } from './PublicQuoteDetails';
const currencyValue = (value: number | null, currency: string, language: string) =>
  value === null
    ? '—'
    : new Intl.NumberFormat(language, { style: 'currency', currency, currencyDisplay: 'code' }).format(value / 10 ** currencyDigits(currency));
function PublicQuoteBlock({
  token,
  view,
  block,
  selection,
  onSelect,
  locked,
}: {
  token: string;
  view: PublicQuoteView;
  block: PublicQuoteSnapshot['blocks'][number];
  selection: PublicQuoteSelection;
  onSelect: (id: string | null) => void;
  locked: boolean;
}) {
  const { t, i18n } = useTranslation(),
    tr = (k: string) => t(`commercialQuotes.publicView.${k}`);
  const [deltas, setDeltas] = useState<Record<string, number | null>>({}),
    [error, setError] = useState(false),
    [visible, setVisible] = useState(false),
    [attempt, setAttempt] = useState(0);
  const holder = useRef<HTMLDivElement>(null),
    selectionKey = JSON.stringify(selection);
  useEffect(() => {
    if (!globalThis.IntersectionObserver) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setVisible(true);
      },
      { rootMargin: '200px' },
    );
    if (holder.current) observer.observe(holder.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    setDeltas({});
    setError(false);
    if (!visible) return;
    const abort = new AbortController();
    void evaluatePublicQuote(token, view.versionId, selection, block.id, abort.signal)
      .then((result) => {
        if (!abort.signal.aborted) setDeltas(Object.fromEntries(result.alternatives.map((a) => [a.optionId, a.deltaMinor])));
      })
      .catch(() => {
        if (!abort.signal.aborted) setError(true);
      });
    return () => abort.abort();
  }, [token, view.versionId, selectionKey, block.id, visible, attempt]);
  return (
    <Box ref={holder}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          {block.title}
        </Typography>
        <Chip size="small" label={tr(block.optional ? 'optional' : 'included')} />
      </Stack>
      <Stack spacing={2}>
        {block.optional && (
          <Button
            disabled={view.archived || locked}
            variant={selection[block.id] === null ? 'outlined' : 'text'}
            onClick={() => onSelect(null)}
          >
            {tr('withoutOptional')}
          </Button>
        )}
        {block.options.map((option) => {
          const chosen = selection[block.id] === option.id,
            delta = deltas[option.id],
            choice = block.options.length > 1,
            showHeading = choice || option.components.length !== 1 || option.title !== option.components[0]?.title;
          return (
            <Paper
              key={option.id}
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                borderColor: chosen ? 'primary.main' : 'divider',
                borderWidth: chosen ? 2 : 1,
              }}
            >
              {showHeading && <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box
                  component={choice ? 'label' : 'div'}
                  sx={{ display: 'flex', alignItems: 'center', cursor: choice && !view.archived ? 'pointer' : 'default' }}
                >
                  {choice && <Radio
                    disabled={view.archived || locked}
                    checked={chosen}
                    onChange={() => onSelect(option.id)}
                    name={block.id}
                    inputProps={{ 'aria-label': option.title }}
                  />}
                  <Typography variant="h6">{option.title}</Typography>
                </Box>
                {choice && chosen && <Chip label={tr('chosen')} color="primary" size="small" />}
              </Stack>}
              <Stack spacing={2}>
                {option.components.map((c) => (
                  <PublicQuoteDetails key={c.id} component={c} showTitle={!(showHeading && option.components.length === 1 && option.title === c.title)} />
                ))}
              </Stack>
              {option.recommendation?.trim() && <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2, overflowWrap: 'anywhere' }}>
                <Typography fontWeight={600}>{t('commercialQuotes.customerText.why')}</Typography>
                <Typography sx={{ whiteSpace: 'pre-line' }}>{option.recommendation}</Typography>
              </Box>}
              {!chosen && (
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={1} sx={{ mt: 2 }}>
                <Button variant="outlined" disabled={view.archived || locked} onClick={() => onSelect(option.id)}>{tr('chooseOption')}</Button>
                <Typography sx={{ textAlign: 'right' }} fontWeight={700}>
                  {delta === undefined
                    ? tr('calculating')
                    : delta === null
                      ? tr('toConfirm')
                      : delta === 0
                        ? tr('samePrice')
                        : `${delta > 0 ? '+' : '−'} ${currencyValue(Math.abs(delta), view.snapshot.currency, i18n.language)} ${tr('forGroup')}`}
                </Typography>
                </Stack>
              )}
            </Paper>
          );
        })}
        {error && (
          <Alert severity="info" action={<Button onClick={() => setAttempt(attempt + 1)}>{tr('retry')}</Button>}>
            {tr('calculationFailed')}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
export function PublicQuote({ token, initialVersion }: { token: string; initialVersion?: string }) {
  const mobile = useMediaQuery('(max-width:899px)');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { t, i18n } = useTranslation(),
    tr = (k: string) => t(`commercialQuotes.publicView.${k}`);
  const [view, setView] = useState<PublicQuoteView>(),
    [selection, setSelection] = useState<PublicQuoteSelection>({}),
    [pricing, setPricing] = useState<PublicQuotePricing>(),
    [loading, setLoading] = useState(true),
    [failed, setFailed] = useState(false),
    [updating, setUpdating] = useState(false),
    [priceFailed, setPriceFailed] = useState(false),
    [changed, setChanged] = useState(false),
    [retry, setRetry] = useState(0),
    [priceAttempt, setPriceAttempt] = useState(0);
  useEffect(() => {
    const abort = new AbortController();
    setLoading(true);
    setFailed(false);
    void fetchPublicQuote(token, abort.signal, retry === 0 ? initialVersion : undefined)
      .then((result) => {
        if (abort.signal.aborted) return;
        setView(result);
        setPricing(result.pricing);
        setSelection(Object.fromEntries(result.snapshot.blocks.map((b) => [b.id, b.selectedOptionId ?? null])));
        setChanged(false);
      })
      .catch(() => {
        if (!abort.signal.aborted) setFailed(true);
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [token, retry, initialVersion]);
  const [failedLogo, setFailedLogo] = useState<string>();
  const selectionKey = JSON.stringify(selection);
  useEffect(() => {
    if (!view) return;
    const abort = new AbortController();
    setUpdating(true);
    setPriceFailed(false);
    void evaluatePublicQuote(token, view.versionId, selection, undefined, abort.signal)
      .then((result) => {
        if (abort.signal.aborted) return;
        setPricing(result.pricing);
        setChanged(result.updated);
        if (result.archived !== view.archived) setView({ ...view, archived: result.archived });
      })
      .catch(() => {
        if (!abort.signal.aborted) setPriceFailed(true);
      })
      .finally(() => {
        if (!abort.signal.aborted) setUpdating(false);
      });
    return () => abort.abort();
  }, [token, view?.versionId, selectionKey, priceAttempt]);
  if (loading) return (
    <Container sx={{ py: 8 }}>
      <CircularProgress aria-label={tr('loading')} />
    </Container>
  );
  if (failed || !view) return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Alert severity="info">{tr('unavailable')}</Alert>
      <Button onClick={() => setRetry(retry + 1)}>{tr('retry')}</Button>
    </Container>
  );
  const { snapshot } = view,
    { brand } = snapshot;
  const money = (n: number | null | undefined) =>
    n === null || n === undefined ? tr('toConfirm') : currencyValue(n, snapshot.currency, i18n.language);
  const phone = brand.phone?.replace(/[^+\d]/g, '');
  const inquiry = quoteContactUrl(
    view.contact?.phone,
    t('commercialQuotes.selection.inquiryMessage', {
      title: snapshot.title,
      version: view.version,
      reference: view.contact?.inquiryReference ?? '',
    }),
  );
  const groupRows = (kind: 'rooms' | 'families') =>
    pricing?.[kind].map((row) => (
      <Stack key={row.id ?? 'none'} direction="row" justifyContent="space-between" gap={1}>
        <Typography variant="body2">{snapshot[kind].find((g) => g.id === row.id)?.name ?? tr(snapshot[kind].length ? 'otherTravelers' : 'travelGroup')}</Typography>
        <Typography fontWeight={600}>{money(row.saleMinor)}</Typography>
      </Stack>
    ));
  const summaryDetails = (
    <Stack spacing={2}>
      {!mobile && <Divider />}
      <Typography fontWeight={700}>{tr('families')}</Typography>
      {groupRows('families')}
      <Box component="details">
        <Box component="summary" sx={{ cursor: 'pointer' }}>
          {tr('rooms')}
        </Box>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {groupRows('rooms')}
        </Stack>
      </Box>
      {Boolean(pricing?.passengerTypes.some((p) => p.id)) && (
        <Box component="details">
          <Box component="summary">{tr('passengerTypes')}</Box>
          {pricing?.passengerTypes.map((p) => (
            <Typography key={p.id ?? 'shared'}>
              {p.id ?? tr('sharedServices')}: {money(p.saleMinor)}
            </Typography>
          ))}
        </Box>
      )}
      <Divider />
      <Typography fontWeight={700}>
        {tr('deposit')}: {money(pricing?.depositMinor)}
      </Typography>
      <Typography variant="body2">{pricing?.depositDueDate ?? tr('dateToConfirm')}</Typography>
      <Typography>
        {tr('balance')}: {money(pricing?.balanceMinor)}
      </Typography>
      <Typography variant="body2">{pricing?.balanceDueDate ?? tr('dateToConfirm')}</Typography>
      <Typography variant="caption">{tr('paymentHint')}</Typography>
      {Boolean(pricing?.destinationCharges.length) && (
        <Box>
          <Typography fontWeight={700}>{tr('atDestination')}</Typography>
          {pricing?.destinationCharges.map((charge, i) => (
            <Typography key={i} variant="body2">
              {currencyValue(charge.amountMinor, charge.currency, i18n.language)}
            </Typography>
          ))}
        </Box>
      )}
    </Stack>
  );
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f7f6fb',
        pb: { xs: 28, md: 6 },
        '& input,& .MuiSelect-select': { fontSize: 16 },
        '& .MuiButton-root': { textTransform: 'none' },
      }}
    >
      <Box
        component="header"
        sx={{ bgcolor: 'white', borderTop: '4px solid', borderTopColor: brand.color ?? 'primary.main', borderBottom: '1px solid', borderBottomColor: 'divider', py: 1.5, position: 'sticky', top: 0, zIndex: 20 }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
              {brand.logoUrl && failedLogo !== brand.logoUrl ? (
                <Box
                  component="img"
                  src={brand.logoUrl}
                  alt={brand.name}
                  onError={() => setFailedLogo(brand.logoUrl ?? undefined)}
                  referrerPolicy="no-referrer"
                  sx={{ width: { xs: 120, sm: 150 }, height: 42, objectFit: 'contain' }}
                />
              ) : <Typography fontWeight={700} sx={{ color: 'text.primary', overflowWrap: 'anywhere' }}>{brand.name}</Typography>}
            </Stack>
            {(inquiry || phone || brand.email) && (
              <Button
                component="a"
                href={inquiry ?? (phone ? `tel:${phone}` : `mailto:${encodeURIComponent(brand.email!.trim())}`)}
                target={inquiry ? '_blank' : undefined}
                rel="noreferrer"
                variant="outlined"
                sx={{ flexShrink: 0, minHeight: 44 }}
              >
                {tr('contact')}
              </Button>
            )}
          </Stack>
        </Container>
      </Box>
      <Container maxWidth="lg" sx={{ pt: { xs: 3, md: 5 } }}>
        <Stack spacing={1} sx={{ mb: 4 }}>
          <Typography color="primary" fontWeight={700}>
            {tr('yourTrip')} · v{view.version}
          </Typography>
          <Typography variant="h3" sx={{ fontSize: { xs: 30, md: 44 }, fontWeight: 800 }}>
            {snapshot.title}
          </Typography>
          <Typography color="text.secondary">
            {snapshot.travelers.length} {tr('travelers')} · {tr('published')}{' '}
            {new Date(view.publishedAt).toLocaleDateString(i18n.language)}
          </Typography>
          <Typography variant="body2">{tr('searchOnly')}</Typography>
        </Stack>
        <PublicQuoteOverview snapshot={snapshot} selection={selection} />
        {view.archived && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {tr('archived')}
          </Alert>
        )}
        {changed && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            action={
              <Button disabled={submitting} onClick={() => setRetry(retry + 1)}>
                {tr('seeLatest')}
              </Button>
            }
          >
            {tr('updated')}
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 330px' },
            gap: 4,
            alignItems: 'start',
          }}
        >
          <Stack spacing={4}>
            {snapshot.blocks.map((block) => (
              <PublicQuoteBlock
                key={block.id}
                token={token}
                view={view}
                block={block}
                selection={selection}
                locked={submitting}
                onSelect={(id) => setSelection((current) => ({ ...current, [block.id]: id }))}
              />
            ))}
          </Stack>
          <Paper
            component="aside"
            variant="outlined"
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              position: { xs: 'fixed', md: 'sticky' },
              top: { md: 100 },
              bottom: { xs: 0, md: 'auto' },
              left: { xs: 0, md: 'auto' },
              right: { xs: 0, md: 'auto' },
              zIndex: 10,
              maxHeight: { xs: '40dvh', md: 'calc(100dvh - 116px)' },
              overflowY: 'auto',
              boxShadow: { xs: 4, md: 0 },
            }}
          >
            <Stack spacing={{ xs: 1, md: 2 }}>
              <Typography variant="h6" fontWeight={700} sx={{ display: { xs: 'none', md: 'block' } }}>
                {tr('summary')}
              </Typography>
              {updating ? (
                <Typography>{tr('calculating')}</Typography>
              ) : priceFailed ? (
                <Alert
                  severity="info"
                  action={<Button onClick={() => setPriceAttempt(priceAttempt + 1)}>{tr('retry')}</Button>}
                >
                  {tr('calculationFailed')}
                </Alert>
              ) : (
                <>
                  <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: 26, md: 34 } }}>
                    {money(pricing?.saleMinor)}
                  </Typography>
                  <Typography variant="caption">{tr('groupTotal')}</Typography>
                  {mobile && (
                    <Button sx={{ py: 0, minHeight: 44 }} aria-haspopup="dialog" onClick={() => setDetailsOpen(true)}>
                      {tr('showSummary')}
                    </Button>
                  )}
                  {!mobile && summaryDetails}
                </>
              )}
              <PublicQuoteActions
                compact={mobile}
                disabled={submitting}
                token={token}
                view={view}
                selection={selection}
                onBusy={setSubmitting}
                onLatest={() => setRetry(retry + 1)}
              />
              {!mobile && (
                <PublicQuotePdf
                  token={token}
                  view={view}
                  selection={selection}
                  disabled={submitting}
                  onBusy={setSubmitting}
                />
              )}
              {!mobile && brand.website && (
                <Link href={brand.website} target="_blank" rel="noreferrer">
                  {tr('agencyWebsite')}
                </Link>
              )}
            </Stack>
          </Paper>
        </Box>
        <PublicQuoteAgency brand={brand} />
      </Container>
      {mobile && (
        <Dialog
          open={detailsOpen}
          onClose={submitting ? undefined : () => setDetailsOpen(false)}
          fullWidth
          maxWidth="sm"
          aria-labelledby="quote-summary-title"
          PaperProps={{ sx: { maxHeight: '80dvh', borderRadius: 3 } }}
        >
          <DialogTitle id="quote-summary-title">{tr('summary')}</DialogTitle>
          <DialogContent dividers>
            {summaryDetails}
            <PublicQuotePdf
              token={token}
              view={view}
              selection={selection}
              disabled={submitting}
              onBusy={setSubmitting}
            />
            {brand.website && (
              <Link href={brand.website} target="_blank" rel="noreferrer">
                {tr('agencyWebsite')}
              </Link>
            )}
          </DialogContent>
          <DialogActions>
            <Button disabled={submitting} onClick={() => setDetailsOpen(false)}>
              {tr('hideSummary')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
